import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchIncomingJoinRequests,
  resolveNotification,
} from '@/lib/apiClient';
import { IncomingJoinRequest } from '@/types';

/**
 * Insider surface for open join requests (Project A, plan §3.7): "X says they
 * work at your practice — invite them". The CTA deep-links into the existing
 * invite modal with the requester's verified email prefilled (the accept path
 * requires an exact email match, so prefill turns "transcribe an email
 * correctly" into "click"). Dismiss resolves the shared CLINIC notification
 * for the whole team. Rendered in AuthenticatedLayout — no clinic-facing
 * notification center exists to reuse.
 */
export function JoinRequestBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['incomingJoinRequests'],
    queryFn: fetchIncomingJoinRequests,
    enabled: !!user && user.clinicIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // clinicId pins which practice the invite is for: the composer otherwise
  // targets the active clinic, so a member of several clinics could add the
  // requester to the wrong one. Matches the link the email and fax channels
  // build server-side (join-request-invite-link.util.ts).
  const handleInvite = useCallback(
    (item: IncomingJoinRequest) => {
      const query = new URLSearchParams({
        inviteEmail: item.requesterEmail,
        clinicId: item.clinicId,
      });
      navigate(`/staff?${query.toString()}`);
    },
    [navigate]
  );

  const handleDismiss = useCallback(
    async (item: IncomingJoinRequest) => {
      try {
        await resolveNotification(item.notificationId);
        await queryClient.invalidateQueries({
          queryKey: ['incomingJoinRequests'],
        });
      } catch {
        toast.error('Failed to dismiss. Please try again.');
      }
    },
    [queryClient]
  );

  const items = data?.data ?? [];
  if (items.length === 0) {
    return null;
  }

  return (
    <div className="px-4 pt-4 space-y-2 lg:px-6">
      {items.map(item => (
        // Stacks on mobile: the message is long and the requester's email is a
        // single unbreakable token, so a phone-width row would squeeze the copy
        // into a sliver beside the buttons. Mirrors DetailPageHeader's
        // actions-below-on-mobile, inline-on-sm+ pattern.
        <div
          key={item.notificationId}
          className="flex flex-col gap-3 rounded-xl border border-border bg-secondary px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex min-w-0 items-start gap-2 sm:items-center">
            <UserPlus
              className="mt-0.5 h-5 w-5 shrink-0 text-primary sm:mt-0"
              aria-hidden
            />
            <p className="text-body-small-normal min-w-0 break-words">
              <span className="font-medium">{item.requesterName}</span> (
              {item.requesterEmail}) says they work at{' '}
              <span className="font-medium">
                {item.clinicName || 'your practice'}
              </span>{' '}
              and wants to join
              {item.bailedOut
                ? ' — they started their own practice in the meantime, but you can still invite them'
                : ' — if you know them, invite them'}
              .
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto sm:gap-1">
            <Button onClick={() => handleInvite(item)} className="sm:h-9">
              Invite
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Dismiss join request from ${item.requesterName}`}
              onClick={() => handleDismiss(item)}
            >
              <X className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
